/**
 * Smooth scrolling.
 *
 * The wheel stops moving the page by a fixed number of pixels per notch and starts
 * moving a target that the page eases toward. Everything else about the scroll is
 * unchanged: this drives the real scroll position of the real document, so
 * `position: sticky` still sticks, the header's intersection observer still fires,
 * the scrollbar is still the scrollbar and still draggable, and the URL and history
 * behave the way the browser makes them behave.
 *
 * **The easing is the one the rest of the site uses.** A lerp is exponential decay:
 * the distance left shrinks by a constant fraction each frame, so the page leaves
 * with no jolt and arrives with no stop — which is the same property `D-65` spent a
 * decision arriving at for the hero's carousel. 0.1 is Lenis's own default and reads
 * as weighted rather than floaty at both 60Hz and 120Hz; Lenis damps it against the
 * frame time, so the feel does not change with the refresh rate.
 *
 * **Five things it deliberately does not do.**
 *
 * 1. **Touch is left alone.** A phone already has momentum scrolling, tuned by the
 *    platform and expected by the hand using it. `syncTouch` stays false, so on a
 *    touch device this file changes nothing at all — which also means it changes
 *    nothing about the configurator's long thumb-heavy scroll, the surface with the
 *    most to lose.
 * 2. **Reduced motion turns it off, not down.** It is never constructed for a visitor
 *    whose device asks for reduced motion, and it is destroyed if that preference
 *    changes mid-visit. That is the pattern `D-62` set for the hero: the still state
 *    is what the file renders, and motion is added to it.
 * 3. **It stands down for anything that locks the page.** Every dialog and drawer in
 *    this theme sets `scroll-lock` on `<html>` (`assets/dialog.js`), so that attribute
 *    is the one signal worth watching: while it is set the overlay owns the wheel and
 *    Lenis is stopped, and the drawer's own scrollable body behaves natively.
 * 4. **It stays out of nested scrollers.** `allowNestedScroll` hands a gesture back to
 *    any element that can scroll in the direction of the gesture — the media gallery,
 *    a cart drawer body — and `gestureOrientation` is vertical, so horizontal
 *    carousels and the slideshow's scroll-snap track never see it.
 * 5. **It does not run in the theme editor.** The editor scrolls the page itself when
 *    a section is selected, and two things animating one scroll position is a fight
 *    with no winner.
 *
 * Anchors are handled here rather than by Lenis's own `anchors` option, for three
 * things that option does not do. It does not call `preventDefault`, so the browser
 * performs the fragment navigation as well and the page jumps before it eases. It
 * does not move focus, which a fragment navigation is required to do and which is the
 * whole of what a skip link is. And it fires on any same-page hash link in the click's
 * path, including a command-click that is opening a second tab — scrolling a page the
 * visitor has just decided to leave.
 *
 * No offset is passed, and that is deliberate: `Lenis.scrollTo` reads the target's own
 * `scroll-margin-top`, which is where this theme already states how far below the
 * sticky header a target should land —
 * `scroll-margin-block-start: var(--header-group-height, var(--scroll-margin))` in
 * `assets/generator.css` and `snippets/product-information-content.liquid`. Passing
 * the offset as well counted the header twice and landed the configurator 138px high;
 * the one place that number is written is the CSS, and it stays there.
 *
 * The whole of this file is a progressive enhancement over a page that already
 * scrolls. Every guard above resolves to "do nothing", and doing nothing leaves the
 * native scroll — and `base.css`'s own `scroll-behavior: smooth` for anchors — exactly
 * as it was.
 *
 * Recorded in `docs/DECISIONS.md` D-67.
 */

import Lenis from 'lenis';

/** @type {MediaQueryList} */
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/** @type {Lenis | null} */
let lenis = null;

/**
 * The link and the element it points at, or null if this click is not one to take
 * over. Everything that is not an ordinary left-click on a fragment of this page is
 * left to the browser: modified clicks, other tabs, other pages, a bare `href="#"`,
 * a fragment nothing on the page answers to, and any link that has opted out with
 * `data-no-smooth-scroll` — which the skip-to-content link does, because a keyboard
 * visitor pressing Enter on it is asking to be somewhere else now.
 *
 * @param {MouseEvent} event
 * @returns {{ link: HTMLAnchorElement, target: Element } | null}
 */
function anchorFor(event) {
  if (event.defaultPrevented || event.button !== 0) return null;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return null;

  const link = /** @type {Element | null} */ (event.target)?.closest?.('a[href]');
  if (!(link instanceof HTMLAnchorElement)) return null;
  if (link.hasAttribute('data-no-smooth-scroll')) return null;
  if (link.target && link.target !== '_self') return null;
  if (link.hash.length < 2) return null;
  if (link.host !== window.location.host || link.pathname !== window.location.pathname) return null;

  const id = decodeURIComponent(link.hash.slice(1));
  const target = document.getElementById(id) ?? document.getElementsByName(id)[0] ?? null;
  return target ? { link, target } : null;
}

/**
 * Move focus the way a fragment navigation would have. The browser does this for free
 * when it handles the anchor itself; having taken the click, this file owes it back.
 *
 * It is owed *immediately*, not when the scroll lands. An eased scroll takes about a
 * second and a half to converge, and it does not converge at all if the visitor
 * touches the wheel on the way — so hanging focus off the arrival would leave a
 * keyboard visitor's focus behind, sometimes for good. `preventScroll` is what makes
 * that safe: focus moves, nothing jumps, and the animation is left as the only thing
 * that is still running.
 *
 * @param {Element} target
 */
function takeFocus(target) {
  if (!(target instanceof HTMLElement)) return;

  const alreadyFocusable = target.hasAttribute('tabindex') || target.matches('a[href], button, input, select, textarea, [contenteditable]');
  if (!alreadyFocusable) target.setAttribute('tabindex', '-1');
  target.focus({ preventScroll: true });
}

/**
 * @param {MouseEvent} event
 */
function onClick(event) {
  if (!lenis) return;

  const anchor = anchorFor(event);
  if (!anchor) return;

  event.preventDefault();
  takeFocus(anchor.target);
  lenis.scrollTo(anchor.target);

  // The history entry a fragment navigation would have written, so Back still goes
  // back. The browser handles the return itself: a hash change from history scrolls
  // natively, which is a jump, which is what going back should be.
  if (window.location.hash !== anchor.link.hash) window.history.pushState(null, '', anchor.link.hash);
}

function start() {
  if (lenis || reducedMotion.matches) return;

  lenis = new Lenis({
    autoRaf: true,
    lerp: 0.1,
    smoothWheel: true,
    syncTouch: false,
    allowNestedScroll: true,
    // Handled by `onClick`, which knows about `scroll-margin` and about focus.
    anchors: false,
  });

  // A dialog may already be open on the frame this runs in — the cart drawer reopens
  // itself after an add-to-cart navigation.
  if (document.documentElement.hasAttribute('scroll-lock')) lenis.stop();
}

function stop() {
  if (!lenis) return;
  lenis.destroy();
  lenis = null;
}

// The theme editor drives the scroll position itself when a section is selected.
if (!window.Shopify?.designMode) {
  start();

  reducedMotion.addEventListener('change', () => (reducedMotion.matches ? stop() : start()));

  new MutationObserver(() => {
    if (!lenis) return;
    if (document.documentElement.hasAttribute('scroll-lock')) lenis.stop();
    else lenis.start();
  }).observe(document.documentElement, { attributes: true, attributeFilter: ['scroll-lock'] });

  document.addEventListener('click', onClick);
}
